import { useEffect, useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Eye, EyeOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSite } from "@/lib/site";

const Row = ({ item, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={`admin-sections-row-${item.key}`}
      className={`flex items-center gap-3 rounded-xl border bg-white px-4 py-3 ${
        isDragging ? "z-10 border-terra shadow-deep" : "border-ink/10"
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        data-testid={`admin-sections-drag-${item.key}`}
        aria-label={`Drag ${item.label}`}
        className="cursor-grab touch-none text-faint hover:text-terra active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <p className="flex-1 text-sm font-medium text-ink">{item.label || item.key}</p>
      <button
        data-testid={`admin-sections-toggle-${item.key}`}
        onClick={() => onToggle(item)}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-outfit text-xs font-semibold ${
          item.visible ? "bg-sagelight text-sage" : "bg-ink/5 text-faint"
        }`}
      >
        {item.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        {item.visible ? "Visible" : "Hidden"}
      </button>
    </div>
  );
};

export const SectionsTab = () => {
  const [items, setItems] = useState([]);
  const [previewKey, setPreviewKey] = useState(0);
  const { refresh } = useSite();

  const load = () => api.get("/admin/content/sections").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    await api.put("/admin/content/sections/reorder", { ids: next.map((i) => i.id) });
    toast.success("Section order updated");
    refresh();
  };

  const toggle = async (item) => {
    await api.put(`/admin/content/sections/${item.id}`, { visible: !item.visible });
    toast.success(item.visible ? "Section hidden" : "Section visible");
    load();
    refresh();
  };

  return (
    <div data-testid="admin-tab-sections">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold text-ink">Homepage sections</h2>
          <p className="mt-1 text-sm text-faint">Drag to reorder, toggle visibility — preview updates on the right.</p>
        </div>
        <button
          data-testid="admin-sections-refresh-preview"
          onClick={() => setPreviewKey((k) => k + 1)}
          className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-4 py-2 font-outfit text-xs font-semibold text-ink hover:border-terra hover:text-terra"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh preview
        </button>
      </div>

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {items.map((item) => (
                <Row key={item.id} item={item} onToggle={toggle} />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <div className="hidden xl:block">
          <p className="mb-2 font-outfit text-xs font-semibold uppercase tracking-wider text-faint">Live page preview</p>
          <div className="overflow-hidden rounded-2xl border border-ink/10 shadow-lift" data-testid="admin-sections-preview">
            <iframe
              key={previewKey}
              src="/"
              title="Homepage preview"
              className="h-[70vh] w-full bg-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
